import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import { Flex } from 'rebass';
import { Label } from '@rebass/forms';
import Button from '../../../../components/Button/Button';
import useNotify from '../../../../hooks/useNotify';
import {
  HazardProtectionStatus,
  HazardProtectionStatusPicker,
} from '../../../../components/WaterService/HazardProtectionStatusPicker';
import { gql } from '../../gql';

type SetHazardProtectionStatusModalProps = {
  onClose: (actionTaken?: boolean) => void;
  serviceIds: string[] | readonly string[];
};

const BULK_SET_HAZARD_PROTECTION_STATUS = gql(`
  mutation BulkSetWaterServiceHazardProtectionStatus(
    $waterServiceIds: [ID!]!
    $status: HazardProtectionStatus!
  ) {
    bulkSetWaterServiceHazardProtectionStatus(
      waterServiceIds: $waterServiceIds
      status: $status
    ) {
      id
      hazardProtectionStatus
    }
  }
`);

const SetHazardProtectionStatusModal: React.FC<
  SetHazardProtectionStatusModalProps
> = ({ onClose, serviceIds: ids }) => {
  const { showError, showSuccess } = useNotify();
  const [status, setStatus] = useState<HazardProtectionStatus | undefined>(
    undefined
  );

  const onCancel = useCallback(() => onClose(), []);

  const [bulkSetStatus, { loading }] = useMutation(
    BULK_SET_HAZARD_PROTECTION_STATUS,
    {
      onCompleted: () => {
        showSuccess(
          `${ids.length} water service${ids.length > 1 ? 's' : ''} updated`
        );
        onClose(true);
      },
      onError: useCallback(
        (error) => {
          showError(
            `Error updating water service${ids.length > 1 ? 's' : ''}`,
            error
          );
        },
        [ids.length]
      ),
    }
  );

  return (
    <Modal title="Set Hazard Protection Status" onClose={onCancel}>
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ minWidth: 300 }}>
          <Label htmlFor="status" mb={1}>
            Hazard Protection Status
          </Label>
          <HazardProtectionStatusPicker
            value={status}
            onChange={(val) =>
              setStatus(val as HazardProtectionStatus | undefined)
            }
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={loading || !status}
          onClick={() => {
            if (!status) {
              showError('Please select a status');
              return;
            }
            bulkSetStatus({
              variables: {
                waterServiceIds: ids as string[],
                status,
              },
            });
          }}
        >
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SetHazardProtectionStatusModal;
